import {
    getAttachmentUrl,
    getMessage,
    getMessageBody,
    listMessages
} from './queries';
import {
    createMessage,
    deleteMessage,
    updateMessage,
    sendEmail as sendEmailMutation,
    generateTestMessages as generateTestMessagesMutation,
} from './mutations';

import { GraphQLQuery } from 'aws-amplify/api';
import {
    CreateMessage,
    DeleteMessage,
    GetAttachmentUrl,
    GetMessage,
    GetMessageBody,
    ListMessages,
    MESSAGE_WRITABLE_FIELDS,
    MessagePatch,
    UpdateMessage
} from "../types.ts";
import {
    showErrorNotifications,
    showSuccessNotification
} from "../../../components/ServiceNotifications.tsx";
import {config} from "../config";
import { client } from '../../email-api-integration';


export const fetchRecords = async (
    {
        aliasId,
        tagId,
        status,
        limit = 10,
        nextToken = null
    }: {
        aliasId?: string
        tagId?: string,
        status?: string,
        limit?: number,
        nextToken?: string | null
    }) => {

    let filter = {}

    if (typeof aliasId !== 'undefined' && aliasId !== null) {
        filter = {
            ...filter,
            ...{aliasId: aliasId}
        };
    }

    if (typeof tagId !== 'undefined' && tagId !== null) {
        filter = {
            ...filter,
            ...{tagIds: [tagId]}
        }
    }

    if (typeof status !== 'undefined' && status !== null) {
        filter = {
            ...filter,
            ...{status: status}
        };
    }

    console.debug('IN FETCH RECORDS. FILTER', filter);

    try {
        const response = await client.graphql<GraphQLQuery<ListMessages>>({
                                                                              query: listMessages,
                                                                              variables: {
                                                                                  filter: filter,
                                                                                  limit: limit,
                                                                                  nextToken: nextToken
                                                                              },
                                                                          });
        const result = {
            items: response.data?.listMessages.items || [],
            nextToken: response.data?.listMessages.nextToken || null
        };
        console.log('fetchRecords returning:', { itemsCount: result.items.length, nextToken: result.nextToken });
        
        return result;
    } catch (e) {
        console.trace(e);
        return { items: [], nextToken: null };
    }
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetMessage>>({
                                                                        query: getMessage,
                                                                        variables: {id: id},
                                                                    });
    return response.data?.getMessage;
};

/**
 * FNP-1532: read a message body through the API.
 *
 * Replaces `downloadData` against S3. The server picks the artifact
 * (text -> parsed -> html, skipping empty ones) and enforces alias ownership, so
 * the caller neither chooses nor sees an S3 key.
 *
 * Returns `contentType` — which artifact was actually served. A caller that needs
 * plain text MUST branch on it; an HTML-only message yields markup.
 */
export const fetchBody = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetMessageBody>>({
        query: getMessageBody,
        variables: {id},
    });
    return response.data?.getMessageBody ?? null;
};

/**
 * FNP-1532: mint a short-lived download URL for one attachment.
 *
 * `index` is the position in the message's own `attachments` array. Call this ON
 * CLICK — minting for every row of a list turns one render into N signatures.
 */
export const fetchAttachmentUrl = async (messageId: string, index: number) => {
    const response = await client.graphql<GraphQLQuery<GetAttachmentUrl>>({
        query: getAttachmentUrl,
        variables: {messageId, index},
    });
    return response.data?.getAttachmentUrl ?? null;
};

export const add = async (payload: object) => {

    try {
        const response = await client.graphql<GraphQLQuery<CreateMessage>>({
                                                                               query: createMessage,
                                                                               variables: {input: payload},
                                                                           });
        console.debug('in the service response', response);

        showSuccessNotification(`${config.titles.singular} created`);
        return response.data?.createMessage;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

// FNP-1672: send the id and the change, never the loaded record. Picking here
// rather than trusting callers keeps a stray `{...record}` from reintroducing
// null key fields on the wire.
export const update = async (id: string, patch: MessagePatch) => {
    const input: MessagePatch & { id: string } = {id};
    for (const field of MESSAGE_WRITABLE_FIELDS) {
        if (patch[field] !== undefined) {
            Object.assign(input, {[field]: patch[field]});
        }
    }

    try {
        const response = await client.graphql<GraphQLQuery<UpdateMessage>>({
                                                                               query: (updateMessage),
                                                                               variables: {input},
                                                                           });

        showSuccessNotification(`${config.titles.singular} updated`);
        return response.data?.updateMessage;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const deleteRecord = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<DeleteMessage>>({
                                                                               query: (deleteMessage),
                                                                               variables: {input: {id: id}},

                                                                           });
        showSuccessNotification(`${config.titles.singular} deleted`);
        return response.data?.deleteMessage;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e)
    }
};

export const sendEmail = async (input: {
    aliasId: string;
    to: string[];
    subject: string;
    htmlBody?: string;
    textBody?: string;
    inReplyTo?: string;
}) => {
    try {
        const response = await client.graphql({
            query: sendEmailMutation,
            variables: { input },
        });

        const result = (response as any).data?.sendEmail;
        if (result) {
            showSuccessNotification('Email sent successfully');
        } else {
            showSuccessNotification('Email queued for sending');
        }
        return result;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e);
    }
};

/**
 * FNP-1521: generate test messages into an alias the caller owns.
 *
 * DEV AND TEST ONLY — see `GenerateTestMessagesButton` for the stage gate on the
 * control, and the API's MessagesResolvers for the gate that actually enforces
 * it. Calling this in prod resolves to null because the field has no resolver
 * there; the UI gate is convenience, the topology gate is the control.
 *
 * Errors are surfaced through the ordinary notification path rather than
 * swallowed: "generate failed because you have no alias yet" is the single most
 * likely outcome on a fresh environment, and it is actionable.
 */
export const generateTestMessages = async (input: {
    count: number;
    aliasId?: string;
    status?: string;
    withAttachments?: boolean;
}) => {
    try {
        const response = await client.graphql({
            query: generateTestMessagesMutation,
            variables: { input },
        });

        const result = (response as any).data?.generateTestMessages;

        if (!result) {
            // FNP-1599. A null field has TWO causes and they need different messages.
            //
            // The prod shape is null data with NO errors: the field is in the schema
            // but has no resolver, so AppSync resolves it to null silently.
            //
            // A server-side failure ALSO leaves the field null, but populates
            // `errors`. Reporting that as "not available in this environment" sends
            // the reader after a deployment problem that does not exist — on test
            // the real cause was "No alias to generate messages into. Create an
            // alias first", which is actionable and was being thrown away.
            // `showErrorNotifications` reads `.errors` off the object it is given,
            // so the response is passed whole rather than the array.
            if ((response as any).errors?.length) {
                showErrorNotifications(response);
                return undefined;
            }

            showErrorNotifications(
                new Error('Test-data generation is not available in this environment')
            );
            return undefined;
        }

        showSuccessNotification(
            `Generated ${result.generated} test ${result.generated === 1 ? 'message' : 'messages'}`
        );
        return result;
    } catch (e) {
        console.trace(e);
        showErrorNotifications(e);
    }
};
